import React, { Component } from "react";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import { Alert, Button, message } from "antd";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";
import ModalProgreso from "../../../../Components/ModalProgreso";
import SeguidorProcesoMasivo from "../../../../Components/seguirProcesoMasivo";
import ResultadosImportarPolizas from "./ResultadosImportarPolizas";
import {
  MAX_MOVIMIENTOS,
  MAX_POLIZAS,
  NOMBRE_PLANTILLA,
  agruparParaEnvio,
  analizarArchivo,
  construirPlantilla,
  prepararCatalogos,
} from "./plantillaImportarPolizas";

// La importación corre en el servidor (lanzarProcesoMasivo) con este nombre de operación.
const OPERACION = "importarPolizas";

const formatoMoneda = (valor) =>
  (Number(valor) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

const instrucciones = [
  <>
    Descarga la plantilla. Va <b>un renglón por movimiento</b> (cargo o abono), no por póliza.
    La columna <b>Póliza</b> agrupa los movimientos: los renglones con el mismo valor forman
    una sola póliza. El número real de la póliza lo asigna el sistema.
  </>,
  <>
    <b>Tipo de Póliza, Fecha y Concepto Póliza</b> se llenan en un renglón de cada póliza; los
    demás pueden ir vacíos. Usa los nombres y códigos de las hojas de catálogo de la plantilla
    (Tipos de Póliza, Cuentas Contables y Proyectos). La fecha va como DD/MM/AAAA.
  </>,
  <>
    El <b>Proyecto</b> de la póliza es el primero que escribas; un renglón sin proyecto usa ése.
    Si una póliza lleva varios proyectos, <b>cada proyecto debe cuadrar por separado</b>, igual
    que en Póliza Manual.
  </>,
  <>
    Cada póliza debe <b>cuadrar</b>. Si uno de sus renglones tiene un error o no cuadra, se
    rechaza <b>la póliza completa</b> y sale en «errores.xlsx»; las demás sí se importan.
  </>,
  <>
    La importación <b>corre en el servidor</b>, una póliza tras otra sin saturarlo. Puedes salir
    de la pantalla: sigue sola y al volver ves su avance. Con <b>Detener</b> termina la póliza en
    curso y se para.
  </>,
  <>
    <b>Volver a subir un archivo que ya se importó duplica las pólizas.</b> Si quedaron pólizas
    sin crear, descarga «No importadas» en los resultados y sube sólo ese archivo.
  </>,
];

const columnasPreview = [
  { title: "Póliza", dataIndex: "poliza", key: "poliza", width: 80 },
  { title: "Tipo", dataIndex: "tipo", key: "tipo", width: 110 },
  {
    title: "Fecha",
    dataIndex: "fecha",
    key: "fecha",
    width: 100,
    render: (valor) => (valor ? moment(valor, "YYYY-MM-DD").format("DD/MM/YYYY") : ""),
  },
  { title: "Concepto", dataIndex: "concepto", key: "concepto", width: 200 },
  { title: "Proyecto", dataIndex: "proyecto", key: "proyecto", width: 150 },
  {
    title: "Cuenta Contable",
    dataIndex: "cuenta",
    key: "cuenta",
    width: 260,
    render: (valor, record) => `${valor}${record.cuentaDescripcion ? ` - ${record.cuentaDescripcion}` : ""}`,
  },
  {
    title: "Cargo",
    dataIndex: "cargo",
    key: "cargo",
    width: 120,
    align: "right",
    render: (valor) => (valor ? formatoMoneda(valor) : ""),
  },
  {
    title: "Abono",
    dataIndex: "abono",
    key: "abono",
    width: 120,
    align: "right",
    render: (valor) => (valor ? formatoMoneda(valor) : ""),
  },
  { title: "Concepto Movimiento", dataIndex: "conceptoMovimiento", key: "conceptoMovimiento", width: 200 },
];

class ImportarPolizas extends Component {
  state = {
    catalogos: null,
    resumen: null,
    modalVisible: false,
    resultado: null,
    // Importación en curso en el servidor.
    corridaId: null,
    progresoVisible: false,
    progreso: null,
    deteniendo: false,
    iniciando: false,
    // La última corrida del usuario, para ofrecer sus resultados al volver a la pantalla.
    ultimaCorrida: null,
    verCorridaAnterior: false,
  };

  componentDidMount() {
    this.seguidor = new SeguidorProcesoMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      onProgreso: (progreso) => {
        if (progreso.operacion && progreso.operacion !== OPERACION) return;
        this.setState({ progreso });
      },
      onFin: () => this.alTerminarCorrida(),
      onError: (mensaje) => {
        message.error(mensaje || "La importación se interrumpió");
        this.alTerminarCorrida();
      },
    });
    this.seguidor.escuchar();
    this.revisarCorridaAnterior();
  }

  componentWillUnmount() {
    this.desmontado = true;
    if (this.seguidor) this.seguidor.detener();
  }

  irAtras = () => {
    if (this.props.location?.state?.volverA) {
      this.props.history.replace(this.props.location.state.volverA);
    } else {
      this.props.history.goBack();
    }
  };

  /**
   * Al entrar: si el usuario tiene una importación corriendo (salió de la pantalla o cerró la
   * pestaña a media corrida) se vuelve a pegar a ella; si ya terminó, se ofrecen sus
   * resultados.
   */
  revisarCorridaAnterior = async () => {
    let corrida = null;
    try {
      const res = await axios.post(
        "polizas/estadoImportacionPolizas",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      );
      corrida = res.data.success === 2 ? res.data.corrida : null;
    } catch (err) {
      return;
    }

    if (!corrida || this.desmontado) return;

    if (!corrida.terminado) {
      const avance = await this.seguidor.consultarEnCurso();
      if (avance && avance.operacion === OPERACION) {
        this.setState({
          corridaId: corrida.corridaId,
          progresoVisible: true,
          progreso: avance,
        });
        return;
      }
      // Lo que esté corriendo es otra operación masiva (o la importación murió con el
      // servidor): no es nuestro modal. Se vuelve a escuchar en limpio.
      this.seguidor.detener();
      this.seguidor.escuchar();
    }

    this.setState({ ultimaCorrida: corrida });
  };

  // El importador guarda los catálogos en su propio estado; aquí también se necesitan para
  // armar el Excel de «No importadas».
  prepararCatalogos = (resData) => {
    const catalogos = prepararCatalogos(resData);
    this.setState({ catalogos });
    return catalogos;
  };

  // Pasada previa sobre todo el archivo: agrupa por póliza y revisa el cuadre. validarFila
  // sólo lee el veredicto de cada fila.
  prepararContextoArchivo = (hojas, catalogos) => {
    const { porFila, resumen } = analizarArchivo(hojas[0]?.data || [], catalogos);
    this.setState({ resumen });
    return { porFila };
  };

  validarFila = (fila, catalogos, indice, contexto) =>
    contexto.porFila?.get(indice) || {
      ok: false,
      errores: [{ campo: "—", motivo: "No se pudo validar la fila" }],
    };

  antesDeImportar = () => {
    const { resumen } = this.state;
    if (resumen && resumen.polizas > MAX_POLIZAS) {
      message.error(`Se importan máximo ${MAX_POLIZAS} pólizas por archivo; parte el archivo en varios`);
      return false;
    }
    if (resumen && resumen.movimientos > MAX_MOVIMIENTOS) {
      message.error(`Se importan máximo ${MAX_MOVIMIENTOS} movimientos por archivo; parte el archivo en varios`);
      return false;
    }
    return true;
  };

  /**
   * Arranca la importación en el servidor. La llama el importador genérico con los renglones
   * válidos (prop `enviarImportacion`); aquí se juntan en pólizas.
   */
  enviarImportacion = async (payload) => {
    if (this.state.iniciando || this.state.progresoVisible) return;
    this.setState({ iniciando: true });

    const polizas = agruparParaEnvio(payload.data);

    let res;
    try {
      res = await axios.post(
        "polizas/iniciarImportacionPolizas",
        { empresaId: this.props.empresaId, polizas },
        { headers: this.props.headersToken },
      );
    } catch (err) {
      this.setState({ iniciando: false });
      message.error(err.response?.data?.message || "No se pudo iniciar la importación");
      return;
    }

    if (res.data.success !== 2) {
      this.setState({ iniciando: false });
      message.error(res.data.message || "No se pudo iniciar la importación");
      return;
    }

    this.setState({
      iniciando: false,
      corridaId: res.data.corridaId,
      progresoVisible: true,
      deteniendo: false,
      ultimaCorrida: null,
      progreso: { procesados: 0, total: res.data.total ?? null, detalle: null },
    });
    this.seguidor.seguir(res.data.procesoId);
  };

  alTerminarCorrida = async () => {
    const { corridaId } = this.state;
    this.setState({ progresoVisible: false, deteniendo: false, progreso: null });
    if (corridaId) await this.cargarResultado(corridaId);
  };

  cargarResultado = async (corridaId) => {
    try {
      const res = await axios.post(
        "polizas/resultadoImportacionPolizas",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron leer los resultados");
        return;
      }
      if (this.desmontado) return;
      this.setState({ resultado: res.data, modalVisible: true, corridaId: null });
    } catch (err) {
      message.error("No se pudieron leer los resultados de la importación");
    }
  };

  detenerImportacion = async () => {
    const { corridaId } = this.state;
    if (!corridaId) return;
    this.setState({ deteniendo: true });
    try {
      const res = await axios.post(
        "polizas/detenerImportacionPolizas",
        { empresaId: this.props.empresaId, corridaId },
        { headers: this.props.headersToken },
      );
      if (res.data.success === 2) {
        message.info("Se detendrá al terminar la póliza en curso");
      } else {
        this.setState({ deteniendo: false });
        message.warning(res.data.message || "No se pudo detener");
      }
    } catch (err) {
      this.setState({ deteniendo: false });
      message.error("No se pudo detener la importación");
    }
  };

  // Sale de la pantalla: la importación sigue en el servidor y al volver se retoma el avance.
  salirSinDetener = () => {
    message.info("La importación sigue en el servidor. Vuelve a esta pantalla para ver su avance y sus resultados.");
    this.setState({ progresoVisible: false });
    this.irAtras();
  };

  // Después de una importación se regresa a Pólizas: la vista previa del archivo sigue
  // cargada y volver a darle Importar duplicaría las pólizas. Si sólo se estaban viendo los
  // resultados de una corrida anterior, se queda en la pantalla.
  cerrarModal = () => {
    const eraCorridaAnterior = this.state.verCorridaAnterior;
    this.setState({ modalVisible: false, resultado: null, verCorridaAnterior: false });
    if (!eraCorridaAnterior) this.irAtras();
  };

  verResultadoCorridaAnterior = async () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return;
    this.setState({ verCorridaAnterior: true });
    await this.cargarResultado(ultimaCorrida.corridaId);
  };

  renderCorridaAnterior = () => {
    const { ultimaCorrida } = this.state;
    if (!ultimaCorrida) return null;

    const cuando = ultimaCorrida.fin || ultimaCorrida.inicio;
    const fecha = cuando ? new Date(cuando).toLocaleString("es-MX") : "";
    const como =
      ultimaCorrida.interrumpida || !ultimaCorrida.terminado
        ? "se interrumpió antes de terminar"
        : ultimaCorrida.detenida
          ? "se detuvo antes de terminar"
          : "terminó";
    const conteo = ultimaCorrida.conteo || {};

    return (
      <Alert
        type={ultimaCorrida.terminado && !ultimaCorrida.interrumpida && !ultimaCorrida.detenida ? "success" : "warning"}
        showIcon
        style={{ marginBottom: 12 }}
        message={`Tu última importación (${fecha}) ${como}: ${conteo.creada || 0} creadas, ${(conteo.error || 0) + (conteo.detenida || 0)} sin crear`}
        description="Puedes ver y descargar sus resultados. Si quedaron pólizas sin crear, descarga «No importadas» y sube sólo ese archivo."
        action={
          <Button size="small" onClick={this.verResultadoCorridaAnterior}>
            Ver resultados
          </Button>
        }
      />
    );
  };

  renderResumen = () => {
    const { resumen } = this.state;
    if (!resumen || (resumen.polizas === 0 && resumen.polizasConError === 0)) return null;

    return (
      <Alert
        type={resumen.polizasConError > 0 ? "warning" : "info"}
        showIcon
        message={
          <>
            <b>{resumen.polizas}</b> pólizas listas para importar ({resumen.movimientos} movimientos, cargos por{" "}
            {formatoMoneda(resumen.cargos)})
            {resumen.polizasConError > 0 && (
              <>
                {" "}
                · <b>{resumen.polizasConError}</b> pólizas rechazadas (vienen en «errores.xlsx»)
              </>
            )}
          </>
        }
      />
    );
  };

  controlesExtra = () => (
    <>
      {this.renderCorridaAnterior()}
      {this.renderResumen()}
    </>
  );

  render() {
    return (
      <>
        <ImportadorExcelGenerico
          {...this.props}
          titulo="Importar Pólizas"
          nombreEntidadPlural="pólizas"
          endpointDatosImportar="polizas/datosParaImportarPolizas"
          nombrePlantilla={NOMBRE_PLANTILLA}
          construirPlantilla={(catalogos) => construirPlantilla(catalogos)}
          prepararCatalogos={this.prepararCatalogos}
          prepararContextoArchivo={this.prepararContextoArchivo}
          validarFila={this.validarFila}
          columnasPreview={columnasPreview}
          instrucciones={instrucciones}
          controlesExtra={this.controlesExtra()}
          antesDeImportar={this.antesDeImportar}
          // La importación corre en el servidor: la pantalla manda las pólizas una sola vez,
          // sigue el avance (ModalProgreso) y al final pide los resultados.
          enviarImportacion={this.enviarImportacion}
        />

        <ModalProgreso
          visible={this.state.progresoVisible}
          titulo="Importando pólizas"
          descripcion="Corre en el servidor, una póliza tras otra sin saturarlo. Puedes salir: sigue sola y al volver a esta pantalla ves su avance."
          procesados={this.state.progreso?.procesados || 0}
          total={this.state.progreso?.total ?? null}
          detalle={this.state.progreso?.detalle || null}
          unidad="pólizas"
          onDetener={this.detenerImportacion}
          deteniendo={this.state.deteniendo}
          onOcultar={this.salirSinDetener}
        />

        <ResultadosImportarPolizas
          visible={this.state.modalVisible}
          resultado={this.state.resultado}
          catalogos={this.state.catalogos}
          cerrarModal={this.cerrarModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  empresaId: state.empresa.id,
  headersToken: state.user.headersToken,
});

export default connect(mapStateToProps)(ImportarPolizas);
